/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Badge, Button,
    Table
} from "reactstrap";
import { listDevices, refreshDevices } from "../../../actions/Devices/actions";
import { listTenants } from "../../../actions/Tenants/actions";
import useConfig, { useEDDateFormats } from "../../../actions/Tenants/config/configHook";
import { Loading } from "../../Helper/Loading";

import { isMoreThanTwoWeeks, isOffline } from "../../../actions/Devices/util";
import { tenantIds } from "../../../actions/Tenants/config/constants";
import DeviceLastSeen from "../../Devices/DeviceLastSeen";
import { FormContainer } from "../../Helper/FormContainer";
import { formatDateTime } from "../Layers/fields/formatters";
import FullPageLayout from "../Layout/FullPageLayout";
import CreateDeviceButton from "./CreateDeviceButton";
import { AvosAction } from "../../../actions/types";

export default function DeviceManagement() {
    const devices = useSelector((state: any) => state.devices.devices);
    const isLoading = useSelector((state: any) => state.devices.isLoading);
    const dispatch = useDispatch();
    const config = useConfig();
    // * EFQ-3850 — admin-only page; always render in ED format.
    const edFormats = useEDDateFormats();

    const dispatchListDevices = () => {
        dispatch(listDevices() as AvosAction);
    };
    const dispatchRefreshDevices = () => {
        dispatch(refreshDevices() as AvosAction);
    };
    // do init load of minis and fetch hearbeat and repeat
    useEffect(() => {
        dispatchListDevices();
        const process = setInterval(dispatchRefreshDevices, 25000);
        return () => clearInterval(process);
    }, []);

    // Fetch list of tenants on component mount
    useEffect(() => {
        if (config.is_admin) {
            dispatch(listTenants() as AvosAction);
        }
    }, []);


    if (isLoading || !devices) {
        return <div css={css`min-height: 100vh;`}>
            <Loading/>
        </div >;
    }

    const headers = (
        <thead>
            <tr>
                <th css={css`width: 160px;`}>Device Name</th>
                <th css={css`width: 60px;`}>Type</th>
                <th css={css`width: 80px;`}>Joined</th>
                <th css={css`width: 80px;`}>Status</th>
                <th css={css`width: 160px;`}>Last activity</th>
                <th css={css`width: 240px;`}>Tenant</th>
                <th className="text-end" css={css`width: 90px;`} ></th>
            </tr>
        </thead>
    );
    const internal_tenants = [tenantIds.ExperienceData, tenantIds.master_tenant, tenantIds.repair_shop];
    const EDdevices = devices.filter((i) => internal_tenants.includes(i.tenant_id) && i.type === "mini");
    const TenantMinis = devices.filter((i) => !internal_tenants.includes(i.tenant_id) && i.type === "mini");
    const otherDevices = devices.filter((i) => i.type !== "mini");
    const buttons = <CreateDeviceButton />;

    return (
        <FullPageLayout>
            <FormContainer fullWidth actionsTop={buttons}>
                <Table size="sm" responsive>
                    {TenantMinis.length > 0 && (
                        <>
                            <thead>
                                <tr>
                                    <td colSpan={7}>
                                        <h3 className="py-3">Avos Mini at Clients</h3>
                                    </td>
                                </tr>
                            </thead>
                            {headers}
                            {TenantMinis.map((i) => <DeviceItem key={`${i.UID}`} i={i} />)}
                        </>
                    )}

                    {EDdevices.length > 0 && (
                        <>
                            <thead>
                                <tr>
                                    <td colSpan={7}>
                                        <h3 className="py-3">Unassigned Avos Mini</h3>
                                    </td>
                                </tr>
                            </thead>

                            {headers}
                            {EDdevices.map((i) => <DeviceItem key={`${i.UID}`} i={i} />)}
                        </>

                    )}

                    {otherDevices.length > 0 && (
                        <>
                            <thead>
                                <tr>
                                    <td colSpan={7}>
                                        <h3 className="py-3">Other Devices</h3>
                                    </td>
                                </tr>
                            </thead>

                            {headers}
                            {otherDevices.map((i) => <DeviceItem key={`${i.UID}`} i={i} />)}
                        </>
                    )}
                </Table>

                <div>
                    <h6 className="text-muted py-3">Last update time: <b>{formatDateTime(dayjs(), edFormats.datetime_format)}</b></h6>
                </div>
            </FormContainer>
        </FullPageLayout>
    );
}

const display_status_of_device = (item) => (isOffline(item) ? "Offline" : "Online");

const DeviceItem = ({ i }) => {
    const navigate = useNavigate();

    const getCellClass = (item) => {
        const isItemOnline = !isOffline(item);
        if (isItemOnline) {
            return "success";
        }

        const delayOverTwoWeeks = isMoreThanTwoWeeks(item);
        if (!delayOverTwoWeeks) {
            return "danger";
        }

        return "light";
    };

    return (
        <tbody>
            <tr css={css`&:hover { cursor: pointer; background: #f8f9fa; }`}>
                <td className="align-middle"><strong>{i.name}</strong></td>
                <td className="align-middle">{i.type}</td>
                <td className="align-middle">{i.joinstatus}</td>
                <td className="align-middle"><Badge color={getCellClass(i)}>{display_status_of_device(i)}</Badge></td>
                <td className="align-middle">
                    <DeviceLastSeen device={i} prefix={false} />
                </td>
                <td className="align-middle">{i.tenant?.tenant_name || "-"} </td>
                <td className="text-end">
                    <Button
                        className="ms-2 my-2"
                        size="sm"
                        color="secondary"
                        outline
                        onClick={() => navigate(`/device-management/${i.UID}`)}
                    >
                        Configuration
                    </Button>
                </td>
            </tr>
        </tbody>
    );
};
